import { computed, ref } from "vue";
import { useStore } from 'vuex';
import Helpers from "../../helpers";

export function usePQR() {
    const store = useStore();
    const spinner = ref(false);
    const showDetail = ref(false);
    const showDeleteConfirm = ref(false);
    const selectedPqrId = ref(null);

    const filter = ref({
        categoria: null,
        estado: null,
        prioridad: null,
        search: null,
        per_page: 15,
    });

    const getPQRs = async (page = 1) => {
        spinner.value = true;
        try {
            const filterData = Helpers.eliminarNull(filter.value);
            await store.dispatch('appPQR/getPQRsAction', { filters: filterData, page });
        } catch (error) {
            console.error('Error fetching PQRs:', error);
        } finally {
            spinner.value = false;
        }
    };

    const getPQRDetail = async (pqrId) => {
        spinner.value = true;
        selectedPqrId.value = pqrId;
        try {
            await store.dispatch('appPQR/getPQRDetailAction', pqrId);
            showDetail.value = true;
        } catch (error) {
            console.error('Error fetching PQR detail:', error);
        } finally {
            spinner.value = false;
        }
    };

    const updatePQRState = async (pqrId, estado, respuesta_admin) => {
        try {
            spinner.value = true;
            await store.dispatch('appPQR/updatePQRStateAction', {
                pqrId,
                estado,
                respuesta_admin,
            });
            return { success: true };
        } catch (error) {
            console.error('Error updating PQR state:', error);
            return { success: false, error: error.message };
        } finally {
            spinner.value = false;
        }
    };

    const updatePQR = async (pqrId, data) => {
        try {
            spinner.value = true;
            await store.dispatch('appPQR/updatePQRAction', { pqrId, data });
            return { success: true };
        } catch (error) {
            console.error('Error updating PQR:', error);
            return { success: false, error: error.message };
        } finally {
            spinner.value = false;
        }
    };

    const deletePQR = async (pqrId) => {
        try {
            spinner.value = true;
            await store.dispatch('appPQR/deletePQRAction', pqrId);
            return { success: true };
        } catch (error) {
            console.error('Error deleting PQR:', error);
            return { success: false, error: error.message };
        } finally {
            spinner.value = false;
        }
    };

    const closeDetail = () => {
        showDetail.value = false;
        store.commit('appPQR/clearPQRDetail');
        selectedPqrId.value = null;
    };

    const confirmDelete = (pqrId) => {
        selectedPqrId.value = pqrId;
        showDeleteConfirm.value = true;
    };

    const executeDelete = async () => {
        const pqrId = selectedPqrId.value;
        if (pqrId) {
            const result = await deletePQR(pqrId);
            if (result.success) {
                closeDeleteConfirm();
                // Recargar lista
                await getPQRs();
            }
        }
    };

    const closeDeleteConfirm = () => {
        showDeleteConfirm.value = false;
        selectedPqrId.value = null;
    };

    const headers = [
        { field: "id", key: 'id', label: "ID" },
        { field: "asunto", label: "Asunto" },
        { field: "categoria", label: "Categoría" },
        { field: "prioridad", label: "Prioridad" },
        { field: "estado", label: "Estado" },
        { field: "user_info", label: "Solicitante" },
        { field: "created_at", label: "Fecha" },
        { field: 'actions', label: "Acciones" }
    ];

    const pqrs = computed(() => store.state.appPQR.pqrs);
    const pqrDetail = computed(() => store.state.appPQR.pqrDetail);
    const loading = computed(() => store.state.appPQR.loading);
    const error = computed(() => store.state.appPQR.error);

    const categoriaOptions = [
        { label: 'Soporte Técnico', value: 'soporte_tecnico' },
        { label: 'Solicitud Administrativa', value: 'solicitud_administrativa' },
        { label: 'Sugerencias de Mejora', value: 'sugerencias_de_mejora' },
    ];

    const estadoOptions = [
        { label: 'Abierto', value: 'abierto' },
        { label: 'En Revisión', value: 'en_revision' },
        { label: 'Resuelto', value: 'resuelto' },
        { label: 'Cerrado', value: 'cerrado' },
    ];

    const prioridadOptions = [
        { label: 'Alta', value: 'alta' },
        { label: 'Media', value: 'media' },
        { label: 'Baja', value: 'baja' },
    ];

    return {
        // State
        filter,
        spinner,
        showDetail,
        showDeleteConfirm,
        selectedPqrId,

        // Computed
        pqrs,
        pqrDetail,
        loading,
        error,
        headers,

        // Options
        categoriaOptions,
        estadoOptions,
        prioridadOptions,

        // Methods
        getPQRs,
        getPQRDetail,
        updatePQRState,
        updatePQR,
        deletePQR,
        closeDetail,
        confirmDelete,
        executeDelete,
        closeDeleteConfirm,
    };
}
